import { JOB_STATUS_CANCELLED } from './constants.js';
import { formatDateDDMMYYYY, normalizeJobStatus, parseEventDateList, parseWorkingTime, } from './dates.js';
import { getWorkingTimeForEventDate } from './payment-schedule.js';
function dateKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function jobId(job) {
    const id = String(job.JobID || '').trim();
    if (id)
        return id;
    const row = Number(job._rowIndex || 0);
    if (row > 0)
        return `${String(job._sheetName || '')}:${row}`;
    return `${String(job.Client || '')}:${String(job['Item / Job Title'] || '')}:${String(job['Event Date'] || '')}`;
}
function toMinutes(time) {
    const parsed = parseWorkingTime(time);
    if (!parsed)
        return null;
    const start = parsed.startHour * 60 + parsed.startMinute;
    const end = parsed.endHour * 60 + parsed.endMinute;
    // Overnight/zero-length schedules are ambiguous in this day-based calendar.
    if (end <= start)
        return null;
    return { start, end };
}
function bookingForDate(job, date) {
    const key = dateKey(date);
    const time = getWorkingTimeForEventDate(job, date);
    const minutes = toMinutes(time);
    const client = String(job['Contact Name'] || job.Client || '').trim() || 'ไม่ระบุลูกค้า';
    const title = String(job['Item / Job Title'] || job['Job Name'] || '').trim() || 'ไม่ระบุชื่องาน';
    return {
        id: jobId(job),
        dateKey: key,
        client,
        title,
        label: `${client} · ${title}`,
        time,
        startMinute: minutes?.start ?? null,
        endMinute: minutes?.end ?? null,
        job,
    };
}
function isActiveJob(job) {
    return normalizeJobStatus(job.Status) !== JOB_STATUS_CANCELLED;
}
function buildIssuePairs(jobs) {
    const issues = [];
    for (let i = 0; i < jobs.length; i++) {
        for (let j = i + 1; j < jobs.length; j++) {
            const first = jobs[i];
            const second = jobs[j];
            if (first.startMinute === null || first.endMinute === null || second.startMinute === null || second.endMinute === null) {
                issues.push({ kind: 'time-unknown', first, second });
            }
            else if (first.startMinute < second.endMinute && second.startMinute < first.endMinute) {
                issues.push({ kind: 'time-overlap', first, second });
            }
        }
    }
    return issues;
}
/** All future or past days that have at least two active jobs; overlap is recorded separately. */
export function buildBusyScheduleDays(jobs) {
    const byDate = new Map();
    for (const job of jobs) {
        if (!isActiveJob(job))
            continue;
        for (const date of parseEventDateList(job['Event Date'])) {
            const key = dateKey(date);
            const dayJobs = byDate.get(key) || [];
            if (!dayJobs.some((booking) => booking.id === jobId(job)))
                dayJobs.push(bookingForDate(job, date));
            byDate.set(key, dayJobs);
        }
    }
    return [...byDate.entries()]
        .filter(([, dayJobs]) => dayJobs.length > 1)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, dayJobs]) => {
        const issues = buildIssuePairs(dayJobs);
        return {
            dateKey: key,
            jobs: dayJobs,
            issues,
            overlapCount: issues.filter((issue) => issue.kind === 'time-overlap').length,
            unknownTimeCount: issues.filter((issue) => issue.kind === 'time-unknown').length,
        };
    });
}
/** Finds same-day bookings and unsafe time collisions for one candidate job. */
export function analyzeJobSchedule(candidate, existingJobs) {
    if (!isActiveJob(candidate))
        return [];
    const candidateId = jobId(candidate);
    const otherJobs = existingJobs.filter((job) => isActiveJob(job) && jobId(job) !== candidateId);
    const candidateDays = parseEventDateList(candidate['Event Date']);
    const conflicts = [];
    for (const date of candidateDays) {
        const key = dateKey(date);
        const candidateBooking = bookingForDate(candidate, date);
        const sameDayBookings = otherJobs.flatMap((job) => parseEventDateList(job['Event Date'])
            .filter((jobDate) => dateKey(jobDate) === key)
            .map((jobDate) => bookingForDate(job, jobDate)));
        if (!sameDayBookings.length)
            continue;
        const dayJobs = [candidateBooking, ...sameDayBookings];
        const issues = buildIssuePairs(dayJobs);
        conflicts.push({
            dateKey: key,
            jobs: dayJobs,
            issues,
            overlapCount: issues.filter((issue) => issue.kind === 'time-overlap').length,
            unknownTimeCount: issues.filter((issue) => issue.kind === 'time-unknown').length,
        });
    }
    return conflicts.sort((left, right) => left.dateKey.localeCompare(right.dateKey));
}
/** Schedule equality ignores display punctuation/spacing but preserves per-day times. */
export function areJobSchedulesEqual(left, right) {
    const getSchedule = (job) => parseEventDateList(job['Event Date'])
        .map((date) => ({
        dateKey: dateKey(date),
        time: getWorkingTimeForEventDate(job, date).replace(/\s+/g, ''),
    }))
        .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    return JSON.stringify(getSchedule(left)) === JSON.stringify(getSchedule(right));
}
export function formatScheduleConflictMessage(days) {
    const lines = ['พบคิวงานในวันเดียวกันที่เวลาทับกัน หรือมีงานที่ยังไม่ระบุเวลา:'];
    for (const day of days) {
        const details = day.issues.map(({ kind, first, second }) => {
            const issue = kind === 'time-overlap' ? 'เวลาทับกัน' : 'เวลาไม่ครบ';
            return `• ${issue}: ${first.label} (${first.time || 'ไม่ระบุเวลา'}) ↔ ${second.label} (${second.time || 'ไม่ระบุเวลา'})`;
        });
        if (details.length)
            lines.push(`วันที่ ${formatDateDDMMYYYY(day.dateKey)}`, ...details);
    }
    lines.push('งานนี้ยังไม่ได้บันทึก — หากต้องการลงคิวต่อ ให้เลือก “บันทึกงานนี้ต่อ”');
    return lines.join('\n');
}
//# sourceMappingURL=schedule-conflicts.js.map